import type { CreateAssetCustomFieldRequestDto } from '@evimo/types/api/asset-custom-field/create-asset-custom-field/create-asset-custom-field.request.dto';
import type { CreateAssetCustomFieldResponseDto } from '@evimo/types/api/asset-custom-field/create-asset-custom-field/create-asset-custom-field.response.dto';
import type { FindAssetCustomFieldResponseDto } from '@evimo/types/api/asset-custom-field/find-asset-custom-field/find-asset-custom-field.response.dto';
import type { UpdateAssetCustomFieldRequestDto } from '@evimo/types/api/asset-custom-field/update-asset-custom-field/update-asset-custom-field.request.dto';
import { buildApiUrl } from '~/app/lib/api-config';

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(buildApiUrl(path), {
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...init?.headers,
    },
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || `Request failed (${response.status})`);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export const AssetCustomFieldService = {
  findByAssetType(
    assetTypeId: string,
  ): Promise<FindAssetCustomFieldResponseDto[]> {
    return apiFetch(`/api/v1/asset-types/${assetTypeId}/custom-fields`);
  },

  create(
    assetTypeId: string,
    dto: Omit<CreateAssetCustomFieldRequestDto, 'assetTypeId'>,
  ): Promise<CreateAssetCustomFieldResponseDto> {
    return apiFetch(`/api/v1/asset-types/${assetTypeId}/custom-fields`, {
      method: 'POST',
      body: JSON.stringify({ ...dto, assetTypeId }),
    });
  },

  update(
    assetTypeId: string,
    id: string,
    dto: UpdateAssetCustomFieldRequestDto,
  ): Promise<FindAssetCustomFieldResponseDto> {
    return apiFetch(`/api/v1/asset-types/${assetTypeId}/custom-fields/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(dto),
    });
  },

  delete(assetTypeId: string, id: string): Promise<void> {
    return apiFetch(`/api/v1/asset-types/${assetTypeId}/custom-fields/${id}`, {
      method: 'DELETE',
    });
  },
};
